import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight, ShieldCheck, Clock, Users, Sparkles } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAM_SIZES = ["1-50 employees", "51-250 employees", "251-1000 employees", "1000+ Enterprise"];
const ROLES = ["Finance / CFO", "Travel Desk Admin", "HR / Operations", "Manager", "Employee / Other"];

const EMPTY = { full_name: "", work_email: "", company_name: "", team_size: "", role: "", message: "" };

const inputCls =
  "w-full px-4 py-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-shadow";

const ASSURANCES = [
  { icon: Clock, text: "45-minute tailored walkthrough with a solutions engineer" },
  { icon: Users, text: "Mapped to your roles, tiers, and approval hierarchies" },
  { icon: ShieldCheck, text: "No commitment. Your data never leaves the demo." },
];

export const DemoForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.team_size || !form.role) {
      toast.error("Please select your team size and role.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/demo-request`, form);
      setSubmitted(res.data);
      toast.success("Demo request received. Our team will reach out within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" data-testid="demo-section" className="bg-white py-24 lg:py-32 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <p className="font-mono text-[11px] tracking-widest text-blue-600 uppercase">Request a demo</p>
          <h2 className="mt-4 font-heading font-extrabold tracking-tight text-slate-900 text-4xl sm:text-5xl">
            See CTEMS on your own travel policy
          </h2>
          <p className="mt-4 text-slate-500 text-base md:text-lg max-w-md">
            Tell us about your team and we will configure a live demo environment with your tier matrix, per diem rules, and approval flows.
          </p>

          <div className="mt-8 p-4 rounded-xl bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-bold text-blue-900 uppercase tracking-wide">Instant Interactive Demo</span>
            </div>
            <p className="text-xs text-blue-800 leading-relaxed mb-3">
              Want to test CTEMS right now? Log in instantly with demo accounts for Admin, Manager, Employee, Travel Desk, or Finance.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-sm transition-all cursor-pointer"
            >
              Launch Live App Demo →
            </Link>
          </div>

          <ul className="mt-8 space-y-4">
            {ASSURANCES.map((a) => (
              <li key={a.text} className="flex items-start gap-4">
                <span className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0 mt-0.5">
                  <a.icon className="w-4 h-4" />
                </span>
                <p className="text-xs text-slate-600 leading-relaxed pt-1.5">{a.text}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 lg:p-10">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                data-testid="demo-form-success"
                className="text-center py-14"
              >
                <span className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </span>
                <h3 className="mt-6 font-heading font-bold text-2xl text-slate-900">
                  Request received, {submitted.full_name.split(" ")[0]}.
                </h3>
                <p className="mt-3 text-slate-500 text-sm max-w-sm mx-auto leading-relaxed">
                  A solutions engineer will contact you at{" "}
                  <span className="font-semibold text-slate-700">{submitted.work_email}</span> within
                  one business day.
                </p>
                <p className="mt-4 font-mono text-[11px] tracking-widest text-slate-400 uppercase">
                  Reference: {submitted.id.slice(0, 8).toUpperCase()}
                </p>
                <button
                  data-testid="demo-form-reset-button"
                  onClick={() => { setForm(EMPTY); setSubmitted(null); }}
                  className="mt-8 px-6 py-2.5 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors duration-200 active:scale-95"
                >
                  Submit another request
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, y: -8 }}
                onSubmit={handleSubmit}
                data-testid="demo-form"
                className="space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="demo-name" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Full name *</label>
                    <input
                      id="demo-name"
                      data-testid="demo-form-name-input"
                      required
                      minLength={2}
                      value={form.full_name}
                      onChange={set("full_name")}
                      placeholder="Priya Sharma"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="demo-email" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Work email *</label>
                    <input
                      id="demo-email"
                      data-testid="demo-form-email-input"
                      type="email"
                      required
                      value={form.work_email}
                      onChange={set("work_email")}
                      placeholder="priya@company.com"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="demo-company" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Company name *</label>
                  <input
                    id="demo-company"
                    data-testid="demo-form-company-input"
                    required
                    minLength={2}
                    value={form.company_name}
                    onChange={set("company_name")}
                    placeholder="Acme Manufacturing Ltd."
                    className={inputCls}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="demo-team-size" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Team size *</label>
                    <Select
                      value={form.team_size}
                      onValueChange={(val) => setForm(f => ({ ...f, team_size: val }))}
                    >
                      <SelectTrigger id="demo-team-size" className="h-11">
                        <SelectValue placeholder="Select team size" />
                      </SelectTrigger>
                      <SelectContent>
                        {TEAM_SIZES.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label htmlFor="demo-role" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Your role *</label>
                    <Select
                      value={form.role}
                      onValueChange={(val) => setForm(f => ({ ...f, role: val }))}
                    >
                      <SelectTrigger id="demo-role" className="h-11">
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label htmlFor="demo-message" className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Specific requirements</label>
                  <textarea
                    id="demo-message"
                    data-testid="demo-form-message-input"
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="E.g. multi-currency per diem, SAP integration, 3-tier approval chain..."
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="demo-form-submit-button"
                  disabled={loading}
                  className="group w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-all duration-200 active:scale-[0.98] disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Request My Demo
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-slate-400">
                  We respond within one business day. No spam, ever.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
